import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    Alert,
    Divider,
    Paper,
} from "@mui/material";
import { WarningAmberOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import useCurrency from "../../../hooks/common/useCurrency";
import { useRefundOrder } from "../../../hooks/orders/useRefundOrder";
import PaymentStatusBadge from "../Table/PaymentStatusBadge";
import FormInput from "../../inputs/FormInput";

// NOTE: The payment-selector RadioGroup stays as raw MUI — no equivalent
// in the inputs/ folder for radio cards.

// Backend RefundService dispatches via these gateways only.
const SUPPORTED_GATEWAYS = new Set(["paymob", "cod", "manual"]);

// Returns the maximum refundable amount on a payment.
const refundableAmount = (payment) => {
    if (!payment) return 0;
    const amount = parseFloat(payment.amount) || 0;
    const refunded = parseFloat(payment.refunded_amount) || 0;
    return Math.max(0, amount - refunded);
};

const RefundModal = ({ open, order, onClose }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const { format: formatPrice } = useCurrency();

    const payments = useMemo(() => order?.payments || [], [order?.payments]);

    const [paymentId, setPaymentId] = useState("");
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");

    const { refundOrderMutate, isRefunding } = useRefundOrder();

    // Default to the most recent completed (paid / partially_refunded) payment.
    useEffect(() => {
        if (!open) return;
        const eligible = [...payments]
            .reverse()
            .find((p) => ["paid", "partially_refunded"].includes(p.status));
        const def = eligible || payments[payments.length - 1];
        if (def) {
            setPaymentId(String(def.id));
            setAmount(refundableAmount(def).toFixed(2));
        }
        setReason("");
    }, [open, payments]);

    const selected = useMemo(
        () => payments.find((p) => String(p.id) === String(paymentId)),
        [payments, paymentId]
    );

    const provider = selected?.provider || selected?.method;
    const supported = provider && SUPPORTED_GATEWAYS.has(provider);
    const max = refundableAmount(selected);
    const amountNum = parseFloat(amount);
    const amountInvalid =
        !Number.isFinite(amountNum) || amountNum <= 0 || amountNum > max;
    const reasonInvalid = !reason || reason.trim().length < 5;
    const submitDisabled =
        !selected || !supported || amountInvalid || reasonInvalid || isRefunding;

    const handlePaymentChange = (id) => {
        setPaymentId(id);
        const p = payments.find((x) => String(x.id) === String(id));
        if (p) setAmount(refundableAmount(p).toFixed(2));
    };

    const submit = () => {
        if (submitDisabled) return;
        refundOrderMutate(
            {
                id: order.id,
                data: {
                    amount: amountNum,
                    reason: reason.trim(),
                    payment_id: parseInt(paymentId, 10),
                },
            },
            { onSuccess: () => onClose?.() }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? "استرداد مبلغ" : "Refund Order"}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    {payments.length === 0 ? (
                        <Alert severity="info">
                            {isArabic
                                ? "لا توجد مدفوعات لهذا الطلب."
                                : "This order has no payments."}
                        </Alert>
                    ) : (
                        <>
                            {/* Payment selector */}
                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                    {isArabic ? "اختر الدفعة" : "Select Payment"}
                                </Typography>
                                <RadioGroup
                                    value={paymentId}
                                    onChange={(e) => handlePaymentChange(e.target.value)}
                                >
                                    <Stack spacing={1}>
                                        {payments.map((p) => {
                                            const refundable = refundableAmount(p);
                                            const supportedP = SUPPORTED_GATEWAYS.has(p.provider || p.method);
                                            return (
                                                <Paper
                                                    key={p.id}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        opacity: refundable > 0 ? 1 : 0.55,
                                                        borderColor: (t) =>
                                                            String(p.id) === String(paymentId)
                                                                ? t.palette.primary.main
                                                                : t.palette.divider,
                                                        borderWidth: String(p.id) === String(paymentId) ? 2 : 1,
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value={String(p.id)}
                                                        disabled={refundable <= 0 || !supportedP}
                                                        control={<Radio />}
                                                        sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                                                        label={
                                                            <Box sx={{ width: "100%" }}>
                                                                <Stack
                                                                    direction="row"
                                                                    justifyContent="space-between"
                                                                    alignItems="center"
                                                                    flexWrap="wrap"
                                                                    gap={1}
                                                                >
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {p.provider || p.method || "-"}
                                                                    </Typography>
                                                                    {p.status && <PaymentStatusBadge status={p.status} />}
                                                                </Stack>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={2}
                                                                    sx={{ mt: 0.5, flexWrap: "wrap" }}
                                                                >
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {isArabic ? "المبلغ:" : "Amount:"} {formatPrice(p.amount ?? 0)}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="warning.main">
                                                                        {isArabic ? "المسترد:" : "Refunded:"}{" "}
                                                                        {formatPrice(p.refunded_amount ?? 0)}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="success.main" fontWeight="bold">
                                                                        {isArabic ? "المتاح:" : "Available:"} {formatPrice(refundable)}
                                                                    </Typography>
                                                                </Stack>
                                                            </Box>
                                                        }
                                                    />
                                                </Paper>
                                            );
                                        })}
                                    </Stack>
                                </RadioGroup>
                            </Box>

                            {selected && !supported && (
                                <Alert severity="warning">
                                    {isArabic
                                        ? `الاسترداد غير مدعوم لطريقة الدفع "${provider}". المدعوم: paymob/cod/manual.`
                                        : `Refund not supported for "${provider}". Supported: paymob/cod/manual.`}
                                </Alert>
                            )}

                            <Divider />

                            {/* Amount */}
                            <FormInput
                                type="number"
                                name="amount"
                                label={isArabic ? "المبلغ" : "Amount"}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                disabled={!selected || !supported}
                                helperText={
                                    selected && supported
                                        ? `${isArabic ? "الحد الأقصى:" : "Max:"} ${formatPrice(max)}`
                                        : ""
                                }
                                error={selected && supported && !!amount && amountInvalid
                                    ? (isArabic ? "مبلغ غير صالح" : "Invalid amount")
                                    : undefined}
                                placeholder=""
                                required
                            />

                            {/* Reason */}
                            <FormInput
                                type="textarea"
                                name="reason"
                                label={isArabic ? "السبب" : "Reason"}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={!selected || !supported}
                                helperText={
                                    reason && reasonInvalid
                                        ? (isArabic ? "5 أحرف على الأقل" : "Minimum 5 characters")
                                        : ""
                                }
                                error={!!reason && reasonInvalid
                                    ? (isArabic ? "5 أحرف على الأقل" : "Minimum 5 characters")
                                    : undefined}
                                required
                                textareaRows="100px"
                                placeholder=""
                            />

                            <Alert
                                severity="warning"
                                icon={<WarningAmberOutlined />}
                                sx={{ borderRadius: 2 }}
                            >
                                {isArabic
                                    ? "هذا الإجراء فوري ولا يمكن التراجع عنه."
                                    : "This action is immediate and cannot be undone."}
                            </Alert>
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isRefunding}>
                    {isArabic ? "إلغاء" : "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={submit}
                    disabled={submitDisabled}
                >
                    {isRefunding
                        ? (isArabic ? "جاري المعالجة..." : "Processing...")
                        : (isArabic ? "تأكيد الاسترداد" : "Confirm Refund")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RefundModal;
